import classnames from 'classnames';
import {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FunctionComponent,
  type KeyboardEvent,
  type MouseEvent,
  type PropsWithChildren,
} from 'react';

import { ArrowDown } from '@/core/tamagoshi/icons/ArrowDown';

import SelectInputContext from './context';
import useOnClickOutside from './hooks/useOnClickOutside';

import styles from './SelectInput.module.scss';

export interface SelectInputProps extends PropsWithChildren {
  id: string;
  optionsNumber: number;
  value?: string;
  label?: string;
  className?: string;
  testId?: string;
  onChange?: (event: any, value: string) => any | void;
}

interface SortChild extends ReactElement {
  props: { value: string };
}
type SortChildren = SortChild[];

export const SelectInput: FunctionComponent<SelectInputProps> = ({
  id,
  optionsNumber,
  value = '',
  label,
  className,
  testId,
  onChange,
  children,
}) => {
  const [selectedOption, setSelectedOption] = useState(value);
  const [optionsVisible, setOptionsVisible] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);

  const selectContainerRef = useRef(null);
  const rootClassNames = classnames([styles.selectInputContainer, className]);

  const resetListState = (visible = false) => {
    setOptionsVisible(visible);
    setFocusedIndex(-1);
  };

  const onHandleClick = () => {
    setOptionsVisible(!optionsVisible);
  };

  const onSelectOption = useCallback(
    (event: KeyboardEvent | MouseEvent, option: string) => {
      setSelectedOption(option);
      setOptionsVisible(false);

      if (onChange) {
        onChange(event, option);
      }
    },
    [onChange],
  );

  const onHandleKeyDown = (event: KeyboardEvent) => {
    event.preventDefault();

    if ((event.key === 'Enter' && !optionsVisible) || event.key === 'Escape') {
      resetListState(!optionsVisible);
    }

    if (event.key === 'Enter' && optionsVisible && focusedIndex >= 0) {
      if (onChange) {
        resetListState();
        setSelectedOption((children as SortChildren)[focusedIndex].props.value);
        onChange(event, (children as SortChildren)[focusedIndex].props.value);
      }
    }

    if (event.key === 'ArrowDown' && optionsVisible) {
      if (focusedIndex === optionsNumber - 1) {
        setFocusedIndex(0);
      } else {
        setFocusedIndex(focusedIndex + 1);
      }
    }

    if (event.key === 'ArrowUp' && optionsVisible) {
      if (focusedIndex === 0 || focusedIndex === -1) {
        setFocusedIndex(optionsNumber - 1);
      } else {
        setFocusedIndex(focusedIndex - 1);
      }
    }
  };

  const clickOutsideHandler = () => {
    resetListState();
  };

  useEffect(() => {
    setSelectedOption(value);
  }, [value]);

  useOnClickOutside(selectContainerRef, clickOutsideHandler);

  const contextValue = useMemo(
    () => ({
      selectedOption,
      focusedOptionIndex: focusedIndex,
      onSelectOption,
    }),
    [focusedIndex, onSelectOption, selectedOption],
  );

  return (
    <SelectInputContext.Provider value={contextValue}>
      <div id={id} className={rootClassNames} ref={selectContainerRef}>
        <div
          role="button"
          tabIndex={0}
          data-testid={testId}
          onClick={onHandleClick}
          onKeyDown={onHandleKeyDown}
          className={styles.selectContainer}
          aria-expanded={optionsVisible}
          aria-controls={`list-${id}`}
        >
          {label} :
          <span className={styles.selectedOption}>{selectedOption}</span>
          <ArrowDown />
        </div>
        <ul
          className={classnames(styles.selectionList, {
            [styles.selectionListVisible]: optionsVisible,
          })}
          data-testid={testId && `${testId}-options`}
          id={`list-${id}`}
          role="tree"
        >
          {children}
        </ul>
      </div>
    </SelectInputContext.Provider>
  );
};

SelectInput.displayName = 'SelectInput';
